import { useEffect } from 'react';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { Map } from '@gonfalon/immutable-shim/immutableShim';
import type { ImmutableServerError } from '@gonfalon/legacy-http-client/httpUtils';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { noop } from 'lodash';

import { fetchFlag as fetchFlagAction } from '#actions/flags';
import { flagByKeySelector } from '#reducers/flags';
import type { Flag } from '#utils/flags/records/Flag';
import { ready, serverError } from '#utils/reduxUtils';

import { useDispatch } from './useDispatch';
import { useSelector } from './useSelector';

const useRedux = (projectKey: string, environmentKey: string, flagKey?: string, expandEvaluationEnvKey?: string) => {
  const flagState = useSelector((state) => {
    if (flagKey) {
      return flagByKeySelector(state, { flagKey });
    }

    return {
      entity: undefined,
      request: undefined,
    };
  });

  const isEvaluationReady = expandEvaluationEnvKey
    ? flagState?.entity?.getConfiguration(expandEvaluationEnvKey)?.evaluation
    : true;
  const isReady = ready(flagState.request) && isEvaluationReady;

  const dispatch = useDispatch();
  const fetchFlag = () => {
    if (flagKey) {
      try {
        return dispatch(
          fetchFlagAction(projectKey, flagKey, environmentKey, { expandEvaluation: !!expandEvaluationEnvKey }),
        );
      } catch {
        noop();
      }
    }
  };

  return {
    isReady,
    flagState,
    fetchFlag,
  };
};

export function useFlagByKey(
  flagKey?: string,
  expandEvaluationEnvKey?: string,
  enable = true,
): { entity: Flag; error: null } | { entity: undefined; error: ImmutableServerError } {
  const projectKey = useProjectKey();
  const environmentKey = useSelectedEnvironmentKey();
  const { isReady, flagState, fetchFlag } = useRedux(projectKey, environmentKey, flagKey, expandEvaluationEnvKey);

  useEffect(() => {
    if (!isReady && enable) {
      fetchFlag()?.then(noop, noop);
    }
  }, [enable]);

  const error = serverError(flagState.request);

  if (!flagState.entity) {
    return { entity: undefined, error: error || (Map() as ImmutableServerError) };
  }

  // Validate that request env is present
  if (flagState.entity.environments.get(environmentKey) === undefined) {
    return { entity: undefined, error: error || (Map() as ImmutableServerError) };
  }

  return { entity: flagState.entity, error: null };
}
